import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  EditNote as EditNoteIcon,
  PhoneIphone as PhoneIphoneIcon,
  Search as SearchIcon,
  VerifiedUser as VerifiedUserIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import api from '../../../api';
import downloadPaperPdf from '../../../components/scan/downloadPaperPdf';

/**
 * Parent → My Purchases → "Mobile plan": everything the child's mobile plan includes
 * (GET parent/plan-library), so the parent can print papers and answers before or after
 * the child sits them. Downloads use the parent routes, which re-check access; answers are
 * always watermarked, question papers only for premium (HBS) papers and every mock exam.
 */

const BRAND = '#3B2A9F';

const SECTIONS = [
  { key: 'paper', label: 'Practice papers', icon: <DescriptionIcon fontSize="small" /> },
  { key: 'mock_exam', label: 'Mock exams', icon: <AssessmentIcon fontSize="small" /> },
  { key: 'worksheet', label: 'Worksheets', icon: <EditNoteIcon fontSize="small" /> },
];

const ukDate = (v) =>
  v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const slug = (s) =>
  String(s || 'download')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'download';

/** [question-paper url, answers url, labels] for an item of a section. */
const downloadsFor = (section, id) => {
  switch (section) {
    case 'mock_exam':
      return {
        question: `parent/mock-exam/${id}/download-pdf`,
        answers: `parent/mock-exam/${id}/download-answer-key`,
        questionLabel: 'Exam paper',
        answersLabel: 'Answers',
      };
    case 'worksheet':
      return {
        question: `parent/ai-tests/${id}/download-pdf?variant=question-paper`,
        answers: `parent/ai-tests/${id}/download-pdf?variant=with-answers`,
        questionLabel: 'Questions',
        answersLabel: 'With answers',
      };
    default:
      return {
        question: `parent/paper/${id}/download-pdf`,
        answers: `parent/paper/${id}/download-answer-key`,
        questionLabel: 'Question paper',
        answersLabel: 'Answers',
      };
  }
};

const metaChips = (section, item) => {
  if (section === 'worksheet') {
    return [item.subject, item.topic, item.question_count ? `${item.question_count} questions` : null];
  }
  return [
    item.school,
    section === 'paper' ? (item.subjects || []).join(', ') : item.category,
    item.duration_minutes ? `${item.duration_minutes} min` : null,
    item.total_marks ? `${item.total_marks} marks` : null,
  ];
};

function PlanBanner({ plan }) {
  const navigate = useNavigate();
  const renews = ukDate(plan?.renews_on);
  const who = plan?.is_child ? `${plan.holder_name}'s` : 'Your';

  return (
    <Card
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2.5,
        borderRadius: 3,
        color: '#fff',
        background: `linear-gradient(135deg, ${BRAND} 0%, #6d5bd0 100%)`,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex' }}>
            <PhoneIphoneIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {plan ? `${who} ${plan.plan_name || 'mobile'} plan` : 'Full library access'}
              {plan?.is_trial && <Chip label="Trial" size="small" sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Every practice paper, mock exam and worksheet below is included. Print the papers and answers whenever
              you need them{renews ? ` · ${plan?.is_trial ? 'trial ends' : 'renews'} ${renews}` : ''}.
            </Typography>
          </Box>
        </Stack>
        {plan && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/parent/billing/mobile')}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', '&:hover': { borderColor: '#fff' } }}
          >
            Manage plan
          </Button>
        )}
      </Stack>
    </Card>
  );
}

function NotCovered() {
  const navigate = useNavigate();
  return (
    <Card elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, border: '1px dashed #c7c2ee', textAlign: 'center', bgcolor: '#FAF9FF' }}>
      <PhoneIphoneIcon sx={{ fontSize: 48, color: BRAND, mb: 1 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        No mobile plan yet
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 2.5 }}>
        A mobile plan for your child includes every practice paper, mock exam and worksheet, and you can print the
        papers and answers from here. Anything you buy separately stays under Purchases.
      </Typography>
      <Button variant="contained" onClick={() => navigate('/parent/billing/mobile')} sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#2f2180' } }}>
        See plans
      </Button>
    </Card>
  );
}

function ItemCard({ section, item, showAnswers, busy, onDownload }) {
  const links = downloadsFor(section, item.id);
  const chips = metaChips(section, item).filter(Boolean);
  const icon = SECTIONS.find((s) => s.key === section)?.icon;

  const button = (which, props) => {
    const key = `${section}-${item.id}-${which}`;
    return (
      <Button
        size="small"
        fullWidth
        disabled={busy === key}
        onClick={() => onDownload(key, links[which], `${slug(item.name)}${which === 'answers' ? '-answers' : ''}.pdf`)}
        startIcon={busy === key ? <CircularProgress size={14} color="inherit" /> : props.icon}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        {...props.button}
      >
        {which === 'answers' ? links.answersLabel : links.questionLabel}
      </Button>
    );
  };

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        transition: 'box-shadow .15s, border-color .15s',
        '&:hover': { borderColor: '#c7c2ee', boxShadow: '0 4px 16px rgba(59,42,159,0.08)' },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: 'rgba(59,42,159,0.08)', color: BRAND, display: 'flex' }}>{icon}</Box>
        <Typography
          sx={{
            fontWeight: 600,
            lineHeight: 1.35,
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.7em',
          }}
          title={item.name}
        >
          {item.name}
        </Typography>
      </Stack>

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, minHeight: 24 }}>
        {chips.map((c) => (
          <Chip key={c} label={c} size="small" sx={{ bgcolor: '#F3F4F6', maxWidth: '100%' }} />
        ))}
        {section === 'paper' && item.watermarked && (
          <Tooltip title="This premium paper carries your child's name on every page">
            <Chip icon={<VerifiedUserIcon />} label="Watermarked" size="small" color="secondary" variant="outlined" />
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
        {button('question', {
          icon: <DownloadIcon />,
          button: { variant: 'contained', sx: { textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: BRAND, '&:hover': { bgcolor: '#2f2180' } } },
        })}
        {showAnswers && button('answers', { icon: <VpnKeyIcon />, button: { variant: 'outlined' } })}
      </Stack>
    </Card>
  );
}

const PER_PAGE = 24;

const PlanLibraryTab = ({ onCovered }) => {
  const [section, setSection] = useState('paper');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ covered: null, plan: null, counts: {}, items: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [showAnswers, setShowAnswers] = useState(true);
  const requestId = useRef(0);

  useEffect(() => {
    api.get('parent/settings')
      .then((res) => setShowAnswers(res.data?.data?.allow_answer_key_download !== false))
      .catch(() => {});
  }, []);

  // Debounce typing so every keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(
    async (page, append) => {
      const id = ++requestId.current;
      append ? setLoadingMore(true) : setLoading(true);
      setError('');
      try {
        const res = await api.get('parent/plan-library', {
          params: { type: section, search: query || undefined, page, per_page: PER_PAGE },
        });
        if (id !== requestId.current) return;
        const d = res.data?.data || {};
        setData((prev) => ({ ...d, items: append ? [...prev.items, ...(d.items || [])] : d.items || [] }));
        onCovered?.(!!d.covered);
      } catch (err) {
        if (id === requestId.current) setError(err.response?.data?.message || 'Could not load your plan library.');
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [section, query, onCovered]
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  const handleDownload = async (key, url, filename) => {
    setBusy(key);
    try {
      await downloadPaperPdf(url, filename);
    } finally {
      setBusy(null);
    }
  };

  if (data.covered === false && !loading) {
    return <NotCovered />;
  }

  const { meta = {}, counts = {}, items = [] } = data;
  const hasMore = (meta.current_page || 1) < (meta.last_page || 1);

  return (
    <Box>
      {data.covered && <PlanBanner plan={data.plan} />}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {SECTIONS.map((s) => {
            const active = section === s.key;
            return (
              <Chip
                key={s.key}
                icon={s.icon}
                label={`${s.label}${counts[s.key] != null ? ` (${counts[s.key]})` : ''}`}
                onClick={() => setSection(s.key)}
                sx={{
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 0.5,
                  bgcolor: active ? BRAND : '#fff',
                  color: active ? '#fff' : '#374151',
                  border: active ? 'none' : '1px solid #e5e7eb',
                  '& .MuiChip-icon': { color: active ? '#fff' : BRAND },
                  '&:hover': { bgcolor: active ? '#2f2180' : '#f3f4f6' },
                }}
              />
            );
          })}
        </Box>
        <TextField
          size="small"
          placeholder={`Search ${SECTIONS.find((s) => s.key === section)?.label.toLowerCase()}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { sm: 260 }, flex: { xs: '1 1 100%', sm: '0 0 auto' }, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
        }}
      >
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                <Skeleton variant="text" width="80%" height={28} />
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="rounded" height={32} sx={{ mt: 1.5 }} />
              </Card>
            ))
          : items.map((item) => (
              <ItemCard
                key={`${section}-${item.id}`}
                section={section}
                item={item}
                showAnswers={showAnswers}
                busy={busy}
                onDownload={handleDownload}
              />
            ))}
      </Box>

      {!loading && items.length === 0 && !error && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {query ? 'Nothing matches your search.' : 'Nothing here yet — new content is added regularly.'}
          </Typography>
        </Box>
      )}

      {!loading && hasMore && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => load((meta.current_page || 1) + 1, true)}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} /> : null}
            sx={{ textTransform: 'none', borderRadius: 999, px: 3 }}
          >
            Show more ({items.length} of {meta.total})
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PlanLibraryTab;
